/**
 * GWP (Gift With Purchase) Handler
 *
 * Auto-removes gift items from cart when no longer eligible for GWP promotion.
 * Shows loading skeleton on gift items during processing.
 *
 * @see https://discountkit.app/discounts/gift-with-purchase#custom-integration
 */

import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/** @typedef {{ key: string, properties?: { _dk_gift?: string }, final_line_price: number, line_price: number }} CartItem */

class GWPGiftHandler {
  #hasGiftsInCart = false;
  #isProcessing = false;

  constructor() {
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    document.addEventListener('discount_kit:gift_adjustments', debounce(this.#onGiftAdjustments, 100));
    this.#checkForGifts();
  }

  async #checkForGifts() {
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      this.#hasGiftsInCart = cart.items.some((/** @type {CartItem} */ item) => item.properties?._dk_gift);
    } catch {
      // Will check again on next event
    }
  }

  #onQuantityChange = () => {
    if (this.#hasGiftsInCart && !this.#isProcessing) {
      this.#isProcessing = true;
      document.body.classList.add('gwp-processing');
    }
  };

  #onGiftAdjustments = async () => {
    if (!this.#isProcessing) {
      this.#isProcessing = true;
      document.body.classList.add('gwp-processing');
    }

    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      this.#hasGiftsInCart = cart.items.some((/** @type {CartItem} */ item) => item.properties?._dk_gift);

      // Find and remove gifts that are no longer free (price > 0)
      const ineligibleGifts = cart.items.filter(
        (/** @type {CartItem} */ item) => item.properties?._dk_gift && (item.final_line_price || item.line_price) > 0
      );

      if (ineligibleGifts.length > 0) {
        await this.#removeGifts(ineligibleGifts);
      }
    } catch (err) {
      console.error('[GWP Handler] Error:', err);
    } finally {
      document.body.classList.remove('gwp-processing');
      this.#isProcessing = false;
    }
  };

  /** @param {CartItem[]} gifts */
  async #removeGifts(gifts) {
    const sectionIds = this.#getSectionIds();

    for (const gift of gifts) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: gift.key,
            quantity: 0,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        });

        if (res.ok) {
          const data = await res.json();
          // Update cart UI
          sectionIds.forEach((id) => data.sections?.[id] && morphSection(id, data.sections[id]));
          document.dispatchEvent(
            new CartUpdateEvent({}, sectionIds[0] || 'cart', {
              itemCount: data.item_count,
              source: 'gwp-gift-handler',
              sections: data.sections,
            })
          );
        }
      } catch (err) {
        console.error('[GWP Handler] Remove gift error:', err);
      }
    }
  }

  /** @returns {string[]} */
  #getSectionIds() {
    /** @type {Set<string>} */
    const ids = new Set();
    document.querySelectorAll('cart-items-component, cart-drawer-component, [data-section-type="main-cart"]').forEach((el) => {
      const sectionId = /** @type {HTMLElement} */ (el).dataset.sectionId;
      if (sectionId) ids.add(sectionId);
    });
    return [...ids];
  }
}

new GWPGiftHandler();
